import { useMemo } from 'react'
import * as actions from '@actions'
import { ROLE_PARSE_ADMIN, ROLE_SUBSCRIPTION_ADMIN } from '@constants/roles'
import {
    CHECK_FIELDS,
    CHECK_STATUSES_PROCESSING,
    CHECK_STATUS_PROCESSED,
    CHECK_STATUS_FAILED,
    CHECK_CAN_BE_RESET_STATUSES,
    HUMAN_STATUSES,
} from '@constants'
import useUserHasRoles from '@hook/useUserHasRoles'
import { toast } from 'react-toastify'
import cn from 'clsx'

import Checkbox from '$components/Forms/Checkbox'
import { Link } from 'react-router-dom'
import { MdDelete, MdMemory, MdOutlineErrorOutline } from 'react-icons/md'
import { formatDate } from '@/utils/date'

const CheckRow = ({
    check,
    hasChecked,
    toggleChecked,
    showMeta = false,
    showDelete = true,
    unmappedChecks = [],
    setMapCheckId,
} = {}) => {
    
    const isParseAdmin = useUserHasRoles([
        ROLE_PARSE_ADMIN,
        ROLE_SUBSCRIPTION_ADMIN,
    ])
    
    const isCheckable = useMemo(() => (
        Boolean(hasChecked && toggleChecked)
    ), [hasChecked, toggleChecked])
    
    const isProcessing = useMemo(() => (
        CHECK_STATUSES_PROCESSING.includes(check.status)
    ), [check])
    
    const formatField = (key, value) => {
        
        if (!value) return ''
        
        switch (key) {
            case 'checkDate': return formatDate(value)
            case 'incomeDate': return formatDate(value)
            default: return value
        }
        
    }
    
    const processCheck = async () => {
        
        try {
            
            await actions.processChecks([check.id])
            
            // Immediately re-fetch the check, since it's status will be reset
            await actions.checkStatuses([check.id])
            
            toast.success('Check queued for processing')
            
        } catch (e) {
            
            console.error('processCheck', e)
            
            toast.error('Unable to process check')
            
        }
        
    }
    
    const deleteCheck = async () => {
        
        try {
            
            await actions.deleteCheck(check.id)
            await actions.fetchChecks()
            
            toast.success('Check deleted')
            
        } catch (e) {
            
            console.error('deleteCheck', e)
            
            toast.error('Failed to delete check')
            
        }
        
    }
    
    const onResetCheckStatusClick = async checkId => {
        
        try {
            await actions.resetCheckStatus(checkId)
            await actions.fetchCheckById(checkId)
        } catch (e) {
            console.error('onResetCheckStatusClick', e)
            toast.error('Failed to reset check status')
        }
        
    }
    
    if (!check) return null
    
    return (
        
        <tr className="CheckRow">
            
            {isCheckable && (
                <td>
                    <Checkbox
                        checked={hasChecked(check.id)}
                        onChange={() => toggleChecked(check.id)} />
                </td>
            )}
            
            <td>
                {showMeta ? (
                    <Link
                        to={`/checks/${check.id}`}
                        title="View check"
                        onClick={e => isProcessing && e.preventDefault()}>
                        {check.name}
                    </Link>
                ) : (check?.name ?? '')}
            </td>
            
            {Object.values(CHECK_FIELDS).map(it => (
                <td key={it.key}>
                    {formatField(it.key, check[it.key])}
                </td>
            ))}
            
            {showMeta && (
                <td>
                    <div className={cn('flex items-center content-center gap-2', {
                        'italic': CHECK_STATUSES_PROCESSING.includes(check.status),
                        'text-red-200': check.status === CHECK_STATUS_FAILED,
                    })}>
                        <span>
                            {HUMAN_STATUSES[check.status] || check.status}
                        </span>
                        {check.status === CHECK_STATUS_FAILED && (
                            <MdOutlineErrorOutline className="text-base" />
                        )}
                    </div>
                </td>
            )}
            
            <td>
                <div className="flex items-center content-center justify-end gap-4">
                    {unmappedChecks?.some(({ id }) => id === check.id) && (
                        <button
                            className="btn, btn-sm btn-secondary whitespace-nowrap"
                            onClick={() => setMapCheckId(check.id)}>
                            Map Fields
                        </button>
                    )}
                    {showMeta && check.status !== 'imported' && (
                        <div className="flex items-center content-center justify-end gap-4">
                            <button
                                className="btn btn-sm btn-secondary flex gap-2 items-center"
                                title={isProcessing ? 'Check is already processing' : ''}
                                disabled={isProcessing || check.status === CHECK_STATUS_PROCESSED}
                                onClick={processCheck}>
                                <MdMemory className="text-lg" />
                                Process
                            </button>
                            {isParseAdmin && CHECK_CAN_BE_RESET_STATUSES.includes(check.status) && (
                                <button
                                    className="flex items-center content-center
                                        justify-end mr-3 btn btn-secondary btn-sm"
                                    onClick={() => onResetCheckStatusClick(check.id)}>
                                    <span>Reset</span>
                                </button>
                            )}
                        </div>
                    )}
                    {showDelete && (
                        <button
                            className="btn btn-sm btn-square"
                            onClick={deleteCheck}>
                            <MdDelete className="text-xl text-red-300" />
                        </button>
                    )}
                </div>
            </td>
        
        </tr>
        
    )
    
}

export default CheckRow
